import React, { useCallback, useState, type ChangeEvent } from "react";
import Details from "./details";
import FilterInputPairs from "./filter-input-pairs";
import type { WorkingFilters } from "components/types/working-filters";
import type { FilterOption } from "components/types/filter-options";
import { debounce } from "helpers/utility-helpers";

type DesignersFilterProps = {
  displayName: FilterOption["displayName"];
  fieldsetHeader: string | null;
  filterOpen: boolean;
  filters: WorkingFilters["designer"];
  idSuffix: string;
  keyName: string;
  onChange: (filter: { filterGroupKey: string; name: string; selected: boolean }) => void;
  onToggleOpenFilters: (filterKey: string, isOpen: boolean) => void;
  values: FilterOption["values"];
};

const DesignersFilter = ({
  displayName,
  fieldsetHeader,
  filterOpen,
  filters,
  idSuffix,
  keyName,
  onChange,
  onToggleOpenFilters,
  values,
}: DesignersFilterProps) => {
  const [searchText, setSearchText] = useState("");

  const debouncedChange = debounce((value: string) => {
    setSearchText(value);
  }, 100);

  const handleChange = (e: ChangeEvent<HTMLInputElement>) => {
    const {
      target: { value },
    } = e;

    debouncedChange(value);
  };

  const onToggle = useCallback(() => {
    onToggleOpenFilters(keyName, !filterOpen);
  }, [filterOpen, keyName, onToggleOpenFilters]);

  const designerRegex = new RegExp(searchText, "i");
  const filteredDesigners = values.filter(({ displayName }) => designerRegex.exec(displayName) !== null);

  const renderResults = () => {
    if (!filteredDesigners.length) {
      return <p>No Results!</p>;
    } else {
      return <FilterInputPairs filters={filters} keyName={keyName} onChange={onChange} values={filteredDesigners} />;
    }
  };

  return (
    <Details
      className="filter-fieldset designers-filter"
      isOpen={filterOpen}
      onToggle={onToggle}
      title={fieldsetHeader ?? displayName}>
      {filterOpen && (
        <div className="designers-wrapper">
          <input
            onChange={handleChange}
            type="text"
            placeholder="Search for a designer"
            id={"designer-search" + idSuffix}
          />
          <div className="designers">{renderResults()}</div>
        </div>
      )}
    </Details>
  );
};

export default DesignersFilter;
